import {
	API
} from "aws-amplify"

import {
	Invoice
} from "@/types"

const API_NAME = "fmDataAPI"

// the guest pay routes live under one path so they read as one logged out area of the API, rather
// than as invoice payment in general. every path under it is literal: Amplify adds a {proxy+} to
// each one, and API Gateway refuses a second variable segment beside it
const GUEST_PAY_PATH = "/guest-pay"
const SESSIONS_PATH = `${GUEST_PAY_PATH}/sessions`
const PAYMENTS_PATH = `${GUEST_PAY_PATH}/payments`
const CONVENIENCE_FEE_PATH = `${GUEST_PAY_PATH}/convenience-fee`

// the Turnstile token travels in a header, the same way the payment token it is traded for does
const TURNSTILE_TOKEN_HEADER = "X-Turnstile-Token"

// the token a successful lookup hands back. the payment routes have no Turnstile of their own, so
// this is what proves the caller got past the one on the lookup
const GUEST_PAY_TOKEN_HEADER = "X-Guest-Pay-Token"

export interface InvoiceLookupPayload {
	invoiceNumber: string
	accountNumber: string
	honeypot: string
	token: string
}

export interface InvoiceLookupResult {
	result: "OK" | "notFound" | "error"
	message?: string
	invoice?: Invoice
	paymentToken?: string
}

// neither the invoice number nor the account number is sent: both travel inside the signed payment
// token, which the Lambda reads them from. nothing the browser can edit decides which invoice is paid
export interface GuestPaymentPayload {
	paymentToken: string
}

export interface PaymentIntentResult {
	result: "OK" | "alreadyPaid" | "notFound" | "error"
	message?: string
	clientSecret?: string
	financialConnectionsClientSecret?: string
	amountDue?: number
	ccFee?: number
}

export interface ConvenienceFeeResult {
	result: "OK" | "notFound" | "error"
	message?: string
	amountDue?: number
}

// Amplify rejects the promise on any non 2xx response, so the message the Lambda sent has to be
// dug out of the thrown error rather than read off a returned body
export function guestPayErrorMessage(error: any): string {
	return error?.response?.data?.message ?? ""
}

// the signed token authorizes every payment call, so it goes in a header the same way the
// Turnstile token does on the lookup
function paymentHeaders(payload: GuestPaymentPayload) {
	return {
		[GUEST_PAY_TOKEN_HEADER]: payload.paymentToken
	}
}

// looks up a single invoice, using the account number and invoice number from the customer's
// emailed invoice in place of a sign in. a POST rather than a GET: the pair works like a password
// and the reply grants a payment token, so the pair goes in the body, never in the URL
export default async function lookupInvoice(payload: InvoiceLookupPayload): Promise<InvoiceLookupResult> {
	return await API.post(API_NAME, SESSIONS_PATH, {
		headers: {
			[TURNSTILE_TOKEN_HEADER]: payload.token
		},
		body: {
			accountNumber: payload.accountNumber,
			invoiceNumber: payload.invoiceNumber,
			honeypot: payload.honeypot
		}
	})
}

// starts a payment. the amount is decided by the Lambda from its own FileMaker read, so nothing
// about the price is sent from here
export async function createGuestPaymentIntent(payload: GuestPaymentPayload): Promise<PaymentIntentResult> {
	return await API.post(API_NAME, PAYMENTS_PATH, {
		headers: paymentHeaders(payload)
	})
}

// adds or removes the card convenience fee and moves the payment intent to the new total
export async function applyGuestConvenienceFee(
	payload: GuestPaymentPayload & { paymentId: string, payingByCard: boolean }
): Promise<ConvenienceFeeResult> {
	return await API.post(API_NAME, CONVENIENCE_FEE_PATH, {
		headers: paymentHeaders(payload),
		body: {
			paymentId: payload.paymentId,
			payingByCard: payload.payingByCard
		}
	})
}

// abandons the payment intent so the visitor can start again with a different payment method
export async function cancelGuestPaymentIntent(
	payload: GuestPaymentPayload & { paymentId: string }
): Promise<ConvenienceFeeResult> {
	return await API.del(API_NAME, PAYMENTS_PATH, {
		headers: paymentHeaders(payload),
		body: {
			paymentId: payload.paymentId
		}
	})
}
